<script setup>
/**
 * A message whose whole content is a single URL renders as a card instead of a
 * bare link: the domain reads first, the full URL sits under it. Nothing is
 * fetched - no favicon, no oEmbed - so the card costs one extra DOM node and
 * no network.
 */
import { computed } from 'vue';

import { MESSAGE_VARIANTS } from '../../constants';
import { useMessageContext } from '../../provider.js';

const props = defineProps({
  url: { type: String, required: true },
});

const { variant } = useMessageContext();

const host = computed(() => {
  try {
    return new URL(props.url).hostname.replace(/^www\./, '');
  } catch {
    return props.url;
  }
});

// The agent bubble is a solid violet surface, every other bubble is a light
// surface, so the card borrows the two palettes rather than a single one.
const isOnBrandSurface = computed(
  () => variant.value === MESSAGE_VARIANTS.AGENT
);

const surfaceClass = computed(() =>
  isOnBrandSurface.value
    ? 'border-white/30 bg-white/15 hover:bg-white/25'
    : 'border-n-strong bg-n-alpha-1 hover:bg-n-alpha-2'
);

const hostClass = computed(() =>
  isOnBrandSurface.value ? 'text-white' : 'text-n-slate-12'
);

const urlClass = computed(() =>
  isOnBrandSurface.value ? 'text-white/75' : 'text-n-slate-11'
);
</script>

<template>
  <a
    :href="url"
    target="_blank"
    rel="noreferrer noopener nofollow"
    class="flex items-center gap-2.5 px-3 py-2 border rounded-xl no-underline transition-colors"
    :class="surfaceClass"
  >
    <span class="shrink-0 i-lucide-globe size-4" :class="urlClass" />
    <span class="flex flex-col min-w-0 grow">
      <span class="text-sm font-medium truncate" :class="hostClass">
        {{ host }}
      </span>
      <span class="text-xs truncate" :class="urlClass">{{ url }}</span>
    </span>
    <span class="shrink-0 i-lucide-arrow-up-right size-3.5" :class="urlClass" />
  </a>
</template>
